/**
 * TODO: Add a function to the Location Contents per Inventory ID (panel header menu item)
 *    to search for same item in other Bin Locations so user can consolidate pieces of same item to a single location
 */

import { Transfer, TransferDetail, TransferService } from '../../acumatica/EntityServices/TransferOrder.service';
import { AudioService } from '../../services/audio.service';
import { Component, ViewChild, AfterViewInit, TemplateRef, isDevMode, ElementRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatTable } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { Adjustment, AdjustmentDetail, AdjustmentService } from 'src/app/acumatica/EntityServices/Adjustment.service';
import { InventorySummaryDetail, InventorySummaryInquiryService } from 'src/app/acumatica/EntityServices/InventorySummaryInquiry.service';
import { StockItem, StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { WarehouseService } from 'src/app/acumatica/EntityServices/Warehouse.service';
import { WarehouseLocationInventoryInquiry, WarehouseLocationInventoryInquiryService } from 'src/app/acumatica/EntityServices/WarehouseLocationInventoryInquiry.service';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { PrinterSelectDialog } from 'src/app/common/printer-selector/printer-selector.component';
import { Sound } from 'src/app/services/audio.service';
import { WMSService } from 'src/app/services/wms.service';

interface IPiece {
  inventoryID: string, lotSerialNbr: string, quantity: number
}
interface LocationItemPiece {
  selected: boolean
  audited: boolean,
  inventoryID: string,
  lotSerialNbr: string,
  QtyAvailable: number,
}
interface IAdjustmentDialogData {
  stockItem: StockItem,
  piece: { QtyAvailable: number },
  adjustment: AdjustmentDetail
}
class LocationItemSet {
  expanded: boolean = false;
  inventoryID: string;
  pieces: LocationItemPiece[];
  altLocationPieces?: InventorySummaryDetail[];
  inventoryItem?: StockItem;

  constructor(inventoryID: string, pieces: LocationItemPiece[]) {
    this.inventoryID = inventoryID;
    this.pieces = pieces;
  }

  /** search for the given Lot # within the collection and return it, or undefined if not found */
  findPiece(lotSerialNbr: string): LocationItemPiece | undefined {
    return this.pieces.find(value => value.lotSerialNbr == lotSerialNbr);
  }

  get auditComplete(): boolean {
    return this.pieces.reduce((accumulator: boolean, piece: LocationItemPiece) => accumulator && piece.audited, true);
  }
  get auditPartial(): boolean {
    return this.pieces.reduce((accumulator: boolean, piece: LocationItemPiece) => accumulator || piece.audited, false);
  }
}
class OverstockCheck {
  get audited(): boolean { return this.adjustment != undefined; };
  item: StockItem;
  inventory: InventorySummaryDetail;
  adjustment?: AdjustmentDetail;
  get Qty(): number { return this.inventory.QtyAvailable + (this.adjustment?.Quantity || 0); }
  // set Qty(value: number) { this.inventory.QtyAvailable = value; }

  constructor(item: StockItem, inventory: InventorySummaryDetail) {
    this.item = item;
    this.inventory = inventory;
  }

  get key(): string { return this.item.InventoryID + '/' + this.inventory.LocationID };
}

const noop = () => { };

@Component({
  templateUrl: './location-audit.component.html',
  styleUrls: ['./location-audit.component.css']
})
export class LocationAuditComponent implements AfterViewInit {

  // #region Construction / Initialization
  pageTitle = "Location Audit";
  isDevMode = isDevMode();
  locationsAudited: string[] = [];

  screenMenuItems = [
    {
      icon: 'upgrade',
      label: 'Update Acumatica',
      handler: this.updateToAcumatica
    },
    {
      icon: 'print',
      label: 'Print Stock Tags',
      handler: () => {
        this.printStockTagQueue().then(noop, noop);
      },
      condition: () => this.stockTagQueue.length > 0
    },
    {
      icon: 'restart_alt',
      label: 'Refresh Warehouse Locations',
      handler: () => {
        this.warehouses.clearCachedLocations(this.warehouseID);
        this.ngAfterViewInit();
      }
    },
    {
      icon: 'adb',
      label: '✔ All in Location',
      condition: isDevMode,
      handler: () => {
        this.locationContents.forEach(item => item.pieces.forEach(piece => piece.audited = piece.inventoryID != 'AC-013' && piece.inventoryID != 'BO-016'));
      }
    }
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private wms: WMSService,
    private warehouses: WarehouseService,
    private stockItemsService: StockItemService,
    private locationInquiryService: WarehouseLocationInventoryInquiryService,
    private inventoryInquiryService: InventorySummaryInquiryService,
    private transferService: TransferService,
    private adjustmentService: AdjustmentService,
    private audio: AudioService,
  ) { }

  @ViewChild('binContentAccordion') binContentAccordion!: ElementRef<MatAccordion>;
  ngAfterViewInit(): void {
    // if (isDevMode()) {
    //   this.warehouseID = "03";
    //   this.locationID = "1S07FD";
    //   this.fetchStockItem("M367-003").then(item => this.queueOverstockInventory(item));
    //   return;
    // }
    // if (isDevMode()) { this.warehouseID = "01"; this.locationID = "202F03"; return; }

    // open the setup dialog to prompt for Warehouse
    this.dialog.open(this.warehouseSelectionDialog).afterClosed().subscribe(warehouseID => {
      if (!warehouseID) { this.router.navigate(['/']); return; }
      this.warehouseID = warehouseID;
      this.resetItemEntryForm();
    });
  }

  get isDirty(): boolean { return this.transferDetails.length > 0; }
  set isDirty(value: boolean) { };
  // #endregion

  // #region UI interface binding
  @ViewChild('warehouseSelectionDialog') warehouseSelectionDialog!: TemplateRef<any>;
  @ViewChild('itemNotInLocationDialog') itemNotInLocationDialog!: TemplateRef<any>;
  @ViewChild('unauditedPiecesDialog') unauditedPiecesDialog!: TemplateRef<any>;

  // piecesTableCols = ['Audited', 'LotSerialNbr', 'Quantity', 'menu'];
  piecesTableCols = (item?: StockItem) => {
    if (item && item.isLotSerItem) return ['Audited', 'LotSerialNbr', 'Quantity', 'menu'];
    return ['Quantity', 'menu']
  }
  unauditedPieceTableCols = ['checked', 'Item', 'Qty'];
  altLocationPiecesTableCols = ['LocationID', 'LotSerialNbr', 'Quantity'];

  /** collapse all expanded Accordion expansion panels */
  closeAllPanels() { this.locationContents.forEach(itemSet => itemSet.expanded = false); }

  onSelectedTabChanged(event: MatTabChangeEvent) {
    this.resetItemEntryForm();
    this.overstockAuditMode = (event.tab.textLabel.substring(0, 9) == 'Overstock');
  }
  // #endregion

  // #region Common component data
  private _warehouseID!: string;
  get warehouseID(): string { return this._warehouseID; }
  set warehouseID(value: string) {
    this._warehouseID = value;
    this.warehouses.getLocations(value).subscribe(locations => this.warehouseLocations = locations);
  }
  private warehouseLocations!: string[];

  private _locationID: string | null = null;
  /** the active / current Location of the loaded locationContents */
  get locationID() { return this._locationID; }
  set locationID(value: string | null) {
    this.finalizeCurrentLocationAudit().then(
      // dialog accepted / nothing un-audited
      () => {
        this._locationID = value;
        this.loadLocationContents();
      },
      // dialog cancelled
      () => { /* silently disregard */ }
    );
  }
  locationContents: LocationItemSet[] = [];

  stockItems: { [key: string]: StockItem } = {};
  stockItemRetrievals: { [key: string]: Promise<StockItem> } = {};

  /** returns an array of LocationItemPieces the user has selected to transfer to LOST */
  buildUnauditedLocationContents() {
    return Object.values(this.locationContents)
      .map(itemSet => itemSet.pieces.filter(piece => !piece.audited && (piece.selected = true)))
      .flat();
  }

  /** return a value indicating if any pieces in the current Location has been audited */
  get anythingAudited(): boolean {
    return Object.values(this.locationContents).map(itemSet => itemSet.pieces.filter(piece => piece.audited)).flat().length > 0;
  }
  // #endregion

  // #region User data entry form
  @ViewChild('inputLegacyTagLotNbr') inputLegacyTagLotNbr!: TemplateRef<any>;
  @ViewChild('inputLegacyLotNbrField') inputLegacyLotNbrField!: ElementRef<any>;

  /** a reference to the legacy 1D entered Stock Item barcode for use in the inputLegacyTagLotNbr dialog */
  _1DBarcodeItem?: StockItem;
  legacyInventoryID?: string;
  /** a reference to the legacy 1D Lot Number entry dialog */
  legacyLotNbrEntryDialog?: MatDialogRef<any, any>;
  /** indicator in Lot Number entry dialog if user wants replacement stock tag printed */
  printLegacyTagReplacements: boolean = true;

  userEntryForm = new FormGroup({
    userInput: new FormControl()
  });

  resetItemEntryForm() {
    this.userEntryForm.reset();
    setTimeout(() => { (<any>this.userEntryForm.get('userInput')).focus(); });
  }

  alertDialog(message: string, resetItemEntryForm: boolean = true) {
    let dialogRef = this.dialog.open(AlertDialog, {
      data: {
        sound: Sound.ErrorBeep,
        message: message,
        buttonText: "OK"
      }
    });
    if (resetItemEntryForm) dialogRef.afterClosed().subscribe(() => { this.resetItemEntryForm(); });
  }

  /** handler for input field being submitted */
  onUserInputEntered(userInput: string | number | object | null) {
    // check if user scanned in a QR code (JSON value)
    if (userInput != null && typeof userInput == 'object') {
      if (!this.locationID) {
        this.alertDialog("Scan Location ID first");
        return;
      }

      this.processUserInput_JSON(userInput);
      return;
    }

    // convert scanner input to working string value
    if (!(userInput = (<string>userInput)?.toUpperCase())) return;

    // check if user scanned in a Bin Location
    if (this.warehouseLocations.contains(userInput)) {
      // user scanned same bin as already selected
      if (this.locationID == userInput) return;

      if (userInput == 'DOCK00') {
        this.dialog.open(ConfirmationDialog, { data: { message: "Are you really sure you want to load all the contents of DOCK00?", confirmText: "Yes, Load it", cancelText: "No, Cancel" } }).afterClosed().subscribe(response => {
          if (response) this.locationID = (<string>userInput)?.toUpperCase();
          this.resetItemEntryForm();
        });
        return;
      }

      // are we doing a required Overstock memo count? -- user indicating overstock bin location
      if (this.overstockAuditMode) {
        this.openOverstockLocationExpansionPanel(userInput);
        return;
      }

      // change location
      this.locationID = userInput;
      this.resetItemEntryForm();
      return;
    }

    // user scanned a single value, not a Location ID -- presumably a legacy tag Inventory ID or Memo/Card item
    if (!this.locationID) {
      this.alertDialog("Scan Location ID first");
      return;
    }
    this.processUserInput_1DInventoryID(userInput);
  }

  /** handler for input value being a JSON / QR barcode -- yardage item */
  processUserInput_JSON(value: any) {
    if (value.PTR) {
      this.printStockTagQueue(value.PTR);
    } else if (value.ITM && value.LOT) {
      this.processInputPiece(value.ITM, value.LOT);
    } else {
      // user scanned something else -- Printer tag?
      this.alertDialog("Invalid QR Code Scanned");
    }
  }

  /** handler for legacy 1D barcode tags on yardage items & memo sample / card items */
  processUserInput_1DInventoryID(inventoryID: string) {
    inventoryID = inventoryID.toUpperCase();
    // note the legacy tag Inventory ID to prevent it accidentally rescanned when expecting lot #
    this.legacyInventoryID = inventoryID;

    // clear the 1DBarcodeItem to prevent previous images showing in dialog while new images are loaded
    this._1DBarcodeItem = undefined;

    // define the callback functions for the fetchStockItem()
    const stockItemReceiver = (stockItem: StockItem) => {
      this._1DBarcodeItem = stockItem;
      if (stockItem.isLotSerItem) {
        // Lot # item -- open the dialog to input the Lot # and optionally print a new Stock Tag
        this.legacyLotNbrEntryDialog = this.dialog.open(this.inputLegacyTagLotNbr);
        this.legacyLotNbrEntryDialog.afterOpened().subscribe(() => {
          this.inputLegacyLotNbrField.nativeElement.focus();
        });
        this.legacyLotNbrEntryDialog.afterClosed().subscribe(() => {
          this.resetItemEntryForm();
        });
      } else {
        // Memo/Card item -- open the quantity adjustment input dialog
        this.inventoryNonLotAdjustment(stockItem);
      }
    };
    const fetchFailedHandler = (err: any) => {
      this.legacyLotNbrEntryDialog?.close();
      this.alertDialog(`Invalid Inventory ID "${inventoryID}"\nNo such Item`);
    }

    if (this.warehouseID == "03") {
      // warehouse "03", try retrieving the item with a prefixed "M" first
      this.fetchStockItem("M" + inventoryID).then(stockItemReceiver, err => {
        // failing that, try it as it was scanned
        this.fetchStockItem(inventoryID).then(stockItemReceiver, fetchFailedHandler);
      });
    } else {
      this.fetchStockItem(inventoryID).then(stockItemReceiver, fetchFailedHandler);
    }
  }

  /** handler for user providing the Lot # in the legacy stock tag input dialog */
  onLegacyInputLotNbr(serialLotNbr: string | number | null) {
    if (serialLotNbr == null) return;
    serialLotNbr = serialLotNbr.toString();
    if (serialLotNbr.indexOf(".") > -1 || serialLotNbr == this.legacyInventoryID) {
      this.inputLegacyLotNbrField.nativeElement.value = null;
      this.inputLegacyLotNbrField.nativeElement.focus();
      this.audio.play(Sound.ErrorBeep);
      this.snackBar.open("Invalid Serial/Lot #", undefined, { duration: 5000 });
      return;
    }

    this.legacyLotNbrEntryDialog?.close();
    this.processInputPiece(this._1DBarcodeItem!.InventoryID, serialLotNbr)
      .then(piece => this.printLegacyTagReplacements && this.queueStockTag(piece))
      .catch(noop);
  }

  /** handler for a yardage item Inventory ID and Lot # provided */
  processInputPiece(inventoryID: string, lotSerialNbr: string): Promise<IPiece> {
    return new Promise<IPiece>((resolve, reject) => {

      // user scanned a stock item tag -- locate the corresponding Item & Lot # in current location contents
      let itemSetIndex = this.locationContents.findIndex(itemset => itemset.inventoryID == inventoryID);
      if (itemSetIndex > -1) {
        // remove the itemSet from the contents
        let itemSet = this.locationContents.splice(itemSetIndex, 1)[0];
        // prepend it at the start of the array list
        this.locationContents.unshift(itemSet);
        // find and mark the piece as audited
        let piece = itemSet.findPiece(lotSerialNbr);
        if (piece) {
          piece.audited = true;
          this.closeAllPanels();
          itemSet.expanded = !itemSet.auditComplete;
          resolve({ inventoryID: piece.inventoryID, lotSerialNbr: piece.lotSerialNbr, quantity: piece.QtyAvailable });
          return;
        }
      }

      this.searchForPieceInInventory(inventoryID, lotSerialNbr)
        .then(piece => resolve(piece))
        .catch(reject);
    });

  }
  // #endregion

  // #region Bin Location Contents / Items

  /** retrieve the contents of the specified location for auditing */
  loadLocationContents() {
    // clear the table from the UI
    this.locationContents = [];

    // get the bin location contents from Acumatica
    this.locationInquiryService.get(new WarehouseLocationInventoryInquiry({
      WarehouseID: this.warehouseID,
      LocationID: this.locationID!
    }),
      { loader: "Getting Location contents" }
    ).subscribe(response => {

      this.resetItemEntryForm();

      // filter out items with zero Qty Available
      response.Details = response.Details.filter(detail => detail.QtyAvailable! > 0);

      // Group & Sort the results by Inventory ID
      response
        .groupResults()
        .forEach(itemSet => this.locationContents.push(new LocationItemSet(itemSet.InventoryID, itemSet.Lots.map(lot => <LocationItemPiece>Object.assign({
          audited: false,
          selected: false,
          inventoryID: itemSet.InventoryID,
          lotSerialNbr: lot.LotSerialNbr,
          QtyAvailable: lot.QtyAvailable,
          QtyOnHand: lot.QtyOnHand,
        })))));
      // sort pieces in each set by Quanitity
      Object.values(this.locationContents).forEach(group => group.pieces = group.pieces.sort((a, b) => a.QtyAvailable - b.QtyAvailable))

      // initialize a single feedback snackbar to be reused (prevent flicker with multiple items)
      let snackbar = this.snackBar.openFromComponent(FeedbackSnackbar);

      // load StockItem data for each Inventory Item in the location sequentially (Promise chain)
      let chain: Promise<any> = Promise.resolve();
      this.locationContents.forEach(itemSet => {
        chain = chain.then(() => {
          let inventoryID = itemSet.inventoryID;
          // get a reference to the Retrieval promise already in progress or start a new one
          let stockItemRetriever = this.fetchStockItem(inventoryID, snackbar);

          // assign a reference to the StockItem to the Item Set
          stockItemRetriever.then(item => itemSet.inventoryItem = item);

          // return the item retrieval promise to be added to the Promise chain
          return stockItemRetriever;
        });
      });

      // close the snackbar after all items loaded
      chain.then(() => snackbar.dismiss());
    });
  }

  /**
   * Load the given Inventory ID Stock Item from Acumatica
   * @param inventoryID the Inventory ID of the item to be retrieved
   * @param snackbarRef a reference to a snackbar to be re-used or undefined to create and destroy one for the specific item
   * @returns 
   */
  fetchStockItem(inventoryID: string, snackbarRef?: MatSnackBarRef<FeedbackSnackbar>): Promise<StockItem> {
    let doDismissSnackbar = false;
    if (snackbarRef === undefined) {
      doDismissSnackbar = true;
      snackbarRef = this.snackBar.openFromComponent(FeedbackSnackbar);
    }

    this.stockItemRetrievals[inventoryID] ||= new Promise<StockItem>((resolve, reject) => {
      // fetch the item from Acumatica
      snackbarRef!.instance.message = `Getting Item ${inventoryID}`;
      this.stockItemsService.get(inventoryID, { loader: null }).subscribe(
        item => resolve(this.stockItems[inventoryID] = item),
        error => reject(error)
      );

    });

    if (doDismissSnackbar) this.stockItemRetrievals[inventoryID].finally(() => snackbarRef!.dismiss());

    return this.stockItemRetrievals[inventoryID];
  }
  // #endregion

  // #region Search for piece outside of Current Bin Location
  pieceSearchResults: InventorySummaryDetail[] = [];
  searchForPieceInInventory(InventoryID: string, LotSerialNbr: string): Promise<IPiece> {
    return new Promise<IPiece>((resolve, reject) => {
      this.pieceSearchResults = [];

      // open dialog that piece needs searched for
      this.audio.play(Sound.ErrorBeep);
      let dialogRef = this.dialog.open(this.itemNotInLocationDialog, { data: { InventoryID: InventoryID, LotSerialNbr: LotSerialNbr } });
      dialogRef.afterClosed().subscribe(result => {
        switch (result) {
          case 'transfer-single':
            let transfer = this.transferInventoryPiece(InventoryID, this.pieceSearchResults[0], this.locationID!);
            if (transfer) resolve({ inventoryID: transfer.InventoryID, lotSerialNbr: transfer.LotSerialNbr!, quantity: transfer.Qty });
            else reject();
            this.resetItemEntryForm();
            break;
          case 'cancel':
            reject();
            this.resetItemEntryForm();
            break;
          case 'invalid-item-lot':
            // this.dialog.open(AlertDialog, { data: { message: "No such Inventory ID / Lot Number could be found", buttonText: 'OK' } })
            //   .afterClosed().subscribe(() => { this.resetItemEntryForm(); reject(); });

            this.adjustInNewPiece(InventoryID, LotSerialNbr).then(adjustment => {
              if (adjustment) resolve({ inventoryID: adjustment.InventoryID!, lotSerialNbr: adjustment.LotSerialNbr!, quantity: adjustment.Quantity });
              else reject();
            })

            break;
          default:
            throw new Error("Unexpected dialog result");
        }
      });

      // perform Acumatica inventory search
      this.inventoryInquiryService.getWhseInventory(this.warehouseID, InventoryID, LotSerialNbr).subscribe(results => {
        if (results.length == 0) {
          dialogRef.close('invalid-item-lot');
        } else if (results.length == 1) {
          if (results[0].LocationID == 'LOST') this.audio.play(Sound.Tada);
          this.pieceSearchResults = results;
        }
      });

    });
  }

  /** search for Inventory Item in other Locations so user can consolidate items */
  searchForMorePiecesOfItem(itemset: LocationItemSet) {
    // perform Acumatica inventory search
    let snackbarRef = this.snackBar.open(`Searching for Item ${itemset.inventoryID}`);
    this.inventoryInquiryService.getWhseInventory(this.warehouseID, itemset.inventoryID, true).subscribe(results => {
      itemset.altLocationPieces = results = results.filter(result => {
        // console.warn(result.LocationID, this.locationID);
        return result.LocationID != this.locationID && result.QtyAvailable > 0;
      });
      snackbarRef.dismiss();

      if (results.length == 0) {
        // this.dialog.open(AlertDialog, { data: { message: `No inventory for item ${itemset.inventoryID}\nfound outside of location ${this.locationID}`, buttonText: 'OK' } });
        this.snackBar.open(`No inventory for item ${itemset.inventoryID} found outside of location ${this.locationID}`, undefined, { duration: 5000 });
      }
    });
  }
  // #endregion

  // #region Inventory Transfers
  @ViewChild('transferDetailsTable') transferDetailsTable!: MatTable<any>;
  transferTableCols = ['InventoryID', 'FromLocation', 'ToLocation', 'Quantity', 'menu'];
  transferDetails: TransferDetail[] = [];

  get transfersTabVisible(): boolean { return this.transferDetails.length > 0; }

  transferInventoryPiece(InventoryID: string, inventoryDetail: InventorySummaryDetail, toLocationID: string): TransferDetail | undefined;
  transferInventoryPiece(InventoryID: string, LotSerialNbr: string, fromLocationID: string, toLocationID: string, Quantity: number): TransferDetail | undefined
  transferInventoryPiece(InventoryID: string, arg2: string | InventorySummaryDetail, arg3: string, arg4?: string, arg5?: number): TransferDetail | undefined {
    let quantity: number;
    let toLocationID: string;
    let fromLocationID: string;
    let lotSerialNbr: string;
    if (typeof (arg2) == 'string') {
      lotSerialNbr = arg2;
      fromLocationID = arg3;
      toLocationID = arg4!;
      quantity = arg5!;
    } else {
      toLocationID = arg3;
      fromLocationID = arg2.LocationID!;
      lotSerialNbr = arg2.LotSerialNbr!;
      quantity = fromLocationID == 'LOST' ? arg2.QtyOnHand : arg2.QtyAvailableForShipment;
    }

    if (fromLocationID == toLocationID) {
      this.alertDialog("Cannot transfer item From and To same Location");
      return undefined;
    }

    // look if there is already a transfer line for this piece
    let existingTransferIndex = this.transferDetails.findIndex(detail => detail.InventoryID == InventoryID && detail.LotSerialNbr == lotSerialNbr && detail.FromLocationID == fromLocationID);
    if (existingTransferIndex != -1) {
      this.transferDetails[existingTransferIndex].ToLocationID = toLocationID;
      this.transferDetailsTable?.renderRows();
      return this.transferDetails[existingTransferIndex];
    }

    // create & add the transfer to the top of the list
    let newTransferDetail = new TransferDetail({
      InventoryID: InventoryID,
      FromLocationID: fromLocationID,
      ToLocationID: toLocationID,
      LotSerialNbr: lotSerialNbr,
      Qty: quantity
    });
    this.transferDetails.unshift(newTransferDetail);
    this.transferDetailsTable?.renderRows();
    return newTransferDetail;
  }

  createTransferDocument(): Transfer {
    let transfer = new Transfer({
      Description: 'Bin Audit - ' + (this.locationsAudited.join(',')),
      FromWarehouseID: this.warehouseID,
      ToWarehouseID: this.warehouseID
    });

    transfer.Details = this.transferDetails;

    return transfer;
  }

  cancelTransferDetail(index: number) {
    this.transferDetails.splice(index, 1);
    this.transferDetailsTable?.renderRows();
  }
  // #endregion

  // #region Inventory Adjustments

  adjustmentDetails: AdjustmentDetail[] = [];
  adjustmentTableCols = ['InventoryID', 'OriginalQty', 'AdjustmentQty', 'NewQty', 'menu'];
  adjustmentTableRefresh = () => this.adjustmentDetails = this.adjustmentDetails.concat();
  @ViewChild('adjustmentDetailsTable') adjustmentDetailsTable!: MatTable<any>;
  @ViewChild('adjustPieceQtyDialog') adjustPieceQtyDialog!: TemplateRef<any>;

  get adjustmentsTabVisible(): boolean { return this.adjustmentDetails.length > 0; }

  private _adjustPieceQtyDialogRef?: MatDialogRef<any>;
  adjustPieceQtyDialogRefOnEnter(adjustment: AdjustmentDetail) {
    if (adjustment.AdjustmentAcceptable) this._adjustPieceQtyDialogRef?.close(true);
  }

  inventoryAdjustmentMenu(itemSet: LocationItemSet, pieceIndex: number) {
    let item = itemSet.inventoryItem!;
    if (item.isLotSerItem) this.inventoryLotItemAdjustment(itemSet, pieceIndex)
    else this.inventoryNonLotAdjustment(item);
  }

  inventoryLotItemAdjustment(itemSet: LocationItemSet, pieceIndex: number) {
    // ensure the Stock Item has been loaded
    this.stockItemRetrievals[itemSet.inventoryID].then(stockItem => {
      // initialize the Adjusment data
      let piece = itemSet.pieces[pieceIndex];
      let adjustment = new AdjustmentDetail({
        WarehouseID: this.warehouseID,
        InventoryID: itemSet.inventoryID,
        LocationID: this.locationID,
        LotSerialNbr: piece.lotSerialNbr,
        OriginalQuantity: piece.QtyAvailable,
        AdjustQtyTo: null,
      });
      let data = {
        stockItem: stockItem,
        piece: piece,
        adjustment: adjustment
      }

      // open the adjustment dialog
      this._adjustPieceQtyDialogRef = this.dialog.open(this.adjustPieceQtyDialog, { data: data });
      this._adjustPieceQtyDialogRef.afterClosed().subscribe(result => {
        if (!result || adjustment.AdjustQtyTo === null) return;

        adjustment.Quantity = (adjustment.AdjustQtyTo - piece.QtyAvailable);
        this.adjustmentDetails.push(adjustment);
        this.adjustmentTableRefresh();
      });
    });
  }

  /** initialize a new AdjustmentDetail or return the existing one for the given Inventory ID & Location ID */
  initGetNonLotAdjustment(inventoryID: string, locationID: string): AdjustmentDetail {
    return this.adjustmentDetails.find(adj => adj.InventoryID == inventoryID && adj.LocationID == locationID)
      || new AdjustmentDetail({
        WarehouseID: this.warehouseID,
        InventoryID: inventoryID,
        LocationID: locationID,
        LotSerialNbr: null,
        AdjustQtyTo: null,
        BypassAdjustmentAcceptableValidation: true
      });
  }

  /** handler for memo/card bardcode item scanned in */
  inventoryNonLotAdjustment(item: StockItem) {
    // ensure the Stock Item has been loaded
    this.stockItemRetrievals[item.InventoryID].then(() => {

      let itemLocationInventory: LocationItemSet | undefined = undefined;
      let overstockItem: OverstockCheck | undefined = undefined;
      let originalQty: number;
      let adjustment: AdjustmentDetail;

      /// Regular Bin Content Item
      if (!this.overstockAuditMode) {
        // locate the item within the current location
        itemLocationInventory = this.locationContents.find(itemSet => itemSet.inventoryID == item.InventoryID);
        // note the quantity currently in the location according to Acumatica
        originalQty = (itemLocationInventory === undefined) ? 0 : itemLocationInventory.pieces[0].QtyAvailable;
        adjustment = this.initGetNonLotAdjustment(item.InventoryID, this.locationID!);
        adjustment.OriginalQuantity = originalQty;

        /// Overstock Required Audit Item
      } else {
        if (this.overstockCheckLocationID === undefined) {
          this.dialog.open(AlertDialog, {
            data: {
              sound: Sound.ErrorBeep,
              message: 'Scan or open the Location ID first',
              icon: 'warning',
              buttonText: 'OK'
            },
          }).afterClosed().subscribe(() => this.resetItemEntryForm());
          return;
        }
        adjustment = this.initGetNonLotAdjustment(item.InventoryID, this.overstockCheckLocationID);
        overstockItem = this.overstockChecks[this.overstockCheckLocationID!]
          .find(overstock => overstock.item.InventoryID == item.InventoryID);

        if (overstockItem == undefined) {
          this.dialog.open(AlertDialog, {
            data: {
              sound: Sound.ErrorBeep,
              message: 'This Item does not need Overstock audited.\nTo audit this Bin Location in its entirety, return to the [Contents] tab and scan the Bin Location',
              icon: 'warning',
              buttonText: 'OK'
            },
          }).afterClosed().subscribe(() => this.resetItemEntryForm());
          return;
        }

        originalQty = overstockItem?.inventory.QtyAvailable;
        adjustment.OriginalQuantity = originalQty;
      }

      let dialogData = {
        stockItem: item,
        piece: { QtyAvailable: originalQty },
        adjustment: adjustment
      }

      // open the adjustment dialog
      this.openItemAdjustmentDialog(dialogData, originalQty).then(() => {
        if (adjustment.AdjustQtyTo == null) {
          // cancel the adjustment          
          this.removeAdjustment(adjustment);
          if (overstockItem) overstockItem.adjustment = undefined;
          return;
        }

        if (itemLocationInventory) itemLocationInventory.pieces[0].audited = true;
        if (overstockItem) { overstockItem.adjustment = adjustment; }

        // add the Overstock adjustment to the list of all adjustments
        if (!this.adjustmentDetails.contains(adjustment)) this.adjustmentDetails.push(adjustment);

        // when the adjustment amount is more than two, and we're counting the primary bin for the item
        // queue that we need to count the overstocks
        if (item.DefaultIssueFrom == this.locationID && adjustment.Quantity >= 2)
          this.queueOverstockInventory(item);

        this.adjustmentTableRefresh();
      }, noop);
    });
  }

  /** opens the 'Item Adjustment' dialog */
  openItemAdjustmentDialog(dialogData: IAdjustmentDialogData, originalQty: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this._adjustPieceQtyDialogRef = this.dialog.open(this.adjustPieceQtyDialog, { data: dialogData });
      this._adjustPieceQtyDialogRef.afterOpened().subscribe(() => {
        let element = this._adjustRemainingOnHandInput.nativeElement;
        element.focus();
        element.select();
      });
      this._adjustPieceQtyDialogRef.afterClosed().subscribe(result => {
        this.resetItemEntryForm();
        // user clicked [Cancel] button
        if (result === false) { reject(); return; }

        let adjustment = dialogData.adjustment;
        adjustment.Quantity = (adjustment.AdjustQtyTo === null) ? 0 : (adjustment.AdjustQtyTo - originalQty);
        resolve();
      });
    });
  }

  @ViewChild('adjustInNewPieceDialog') adjustInNewPieceDialog!: TemplateRef<any>;
  @ViewChild('adjustRemainingOnHandInput') _adjustRemainingOnHandInput!: ElementRef<any>;
  adjustInNewPiece(InventoryID: string, LotSerialNbr: string): Promise<AdjustmentDetail | null> {
    return new Promise<AdjustmentDetail | null>(resolve => {
      let adjustment = new AdjustmentDetail({
        InventoryID: InventoryID,
        LotSerialNbr: LotSerialNbr,
        WarehouseID: this.warehouseID,
        LocationID: this.locationID,
        OriginalQuantity: 0,
        Quantity: null,
        acceptAdjustmentsLessThanYards: 70
      });

      this.dialog.open(this.adjustInNewPieceDialog, { data: { adjustment: adjustment } })
        .afterClosed().subscribe(response => {
          this.resetItemEntryForm();
          if (response) {
            this.adjustmentDetails.push(adjustment);
            this.adjustmentDetailsTable?.renderRows();
            resolve(adjustment);
          }
          else resolve(null);
        });
    });
  }

  editAdjustment(adjustmentIndex: number) {
    let adjustment = this.adjustmentDetails[adjustmentIndex];
    console.warn(adjustment);
    // this.dialog.open(AlertDialog, { data: { message: "TODO" } });
    this.processUserInput_1DInventoryID(adjustment.InventoryID!);
  }

  cancelAdjustment(adjustmentIndex: number) {
    this.dialog.open(ConfirmationDialog, {
      data: {
        message: "Cancel Adjustment Detail?",
        confirmText: 'Delete the Adjustment',
        cancelText: 'Keep the Adjustment'
      }
    }).afterClosed().subscribe(response => {
      if (response) this.removeAdjustment(this.adjustmentDetails[adjustmentIndex]);
    });
  }

  /** remove the Adjustment from the adjustmentDetails collection and any Overstock Checks */
  removeAdjustment(adjustment: AdjustmentDetail) {
    this.adjustmentDetails.remove(adjustment);

    // if the adjustment was assigned to a Overstock check, clear it
    Object.keys(this.overstockChecks).forEach(locID => {
      this.overstockChecks[locID]
        .filter(overstock => overstock.adjustment == adjustment)
        .forEach(overstock => overstock.adjustment = undefined)
    })
    this.adjustmentTableRefresh();
  }

  createAdjustmentDocument(): Adjustment {
    let adjustment = new Adjustment({
      Description: 'Bin Audit - ' + (this.locationsAudited.join(',')),
    });

    adjustment.Details = this.adjustmentDetails;

    return adjustment;
  }

  // #endregion

  // #region Stock Tag Printing
  @ViewChild('stockTagQueueTable') stockTagQueueTable!: MatTable<any>;
  printTagQueueTableCols = ['InventoryID', 'LotSerialNbr', 'Quantity', 'menu']
  stockTagQueue: { inventoryID: string, lotSerialNbr: string, quantity: number }[] = [];

  /** create a Stock Tag print job for the indicated piece */
  queueStockTag(piece: IPiece): void;
  queueStockTag(inventoryID: string, lotSerialNbr: string, quantity: number): void;
  queueStockTag(arg1: IPiece | string, arg2?: string, arg3?: number): void {
    if (typeof arg1 != 'string') {
      arg2 = arg1.lotSerialNbr;
      arg3 = arg1.quantity;
      arg1 = arg1.inventoryID;
    }

    this.stockTagQueue.push({
      inventoryID: arg1,
      lotSerialNbr: arg2!,
      quantity: arg3!
    });
    this.stockTagQueueTable?.renderRows();
    this.resetItemEntryForm();
  }

  /** cancel the indicated Stock Tag print job from the queue */
  cancelStockTag(tagIndex: number) {
    this.stockTagQueue.splice(tagIndex, 1);
    this.stockTagQueueTable?.renderRows();
  }

  /** send the queued Stock Tag print jobs to Acumatica sequentially */
  printStockTagQueue(printer?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      if (this.stockTagQueue.length == 0) {
        this.snackBar.open("No Stock Tags have been requested", undefined, { duration: 5000 });
        return;
      }

      if (printer === undefined) {
        this.dialog.open(PrinterSelectDialog)
          .afterClosed().subscribe((printer: any) => {
            if (printer?.Printer) {
              this.printStockTagQueue(printer?.Printer.id);
              resolve();
            } else {
              reject();
            }
          });
      } else {
        // initialize a single feedback snackbar to be reused
        let snackbar = this.snackBar.openFromComponent(FeedbackSnackbar);

        let sendNextTagPrint = () => {
          let tag = this.stockTagQueue.shift();
          if (tag === undefined) {
            this.stockTagQueueTable?.renderRows();
            snackbar.dismiss();
            resolve();
            return;
          }

          snackbar.instance.message = `Printing ${tag.inventoryID} / ${tag.lotSerialNbr}`;
          this.wms.printStockRollTag(
            printer,
            tag.inventoryID,
            tag.lotSerialNbr,
            tag.quantity,
            undefined, undefined, 1, null, null
          ).subscribe(() => sendNextTagPrint());
        };
        sendNextTagPrint();
      };

    });
  }
  // #endregion

  // #region Location Completion

  /** Handle un-audited items in the current bin location by opening a dialog for the user to select pieces to transfer to "LOST" */
  finalizeCurrentLocationAudit(): Promise<void> {
    if (!this.locationID) return Promise.resolve();
    if (!this.locationsAudited.contains(this.locationID)) this.locationsAudited.push(this.locationID);
    if (!this.anythingAudited || this.warehouseID == "03") return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      let unaudited = this.buildUnauditedLocationContents();
      if (unaudited.length > 0) {
        this.dialog.open(this.unauditedPiecesDialog, { data: unaudited }).afterClosed().subscribe((result?: LocationItemPiece[]) => {
          if (result === undefined) { reject(); return; }

          result
            .filter(piece => piece.selected)
            .forEach(piece => this.transferInventoryPiece(piece.inventoryID, piece.lotSerialNbr, this.locationID!, "LOST", piece.QtyAvailable));

          resolve();
        });
      } else {
        resolve();
      }
    });
  }


  // #endregion

  // #region Update to Acumatica
  updateToAcumatica() {
    if (this.overstockItemsPending > 0) {
      this.dialog.open(AlertDialog, { data: { message: "Please audit the indicated 'Overstock' items before proceeding", buttonText: 'OK' } });
      return;
    }

    this.finalizeCurrentLocationAudit().then(() => {

      let printPendingStockTags = this.stockTagQueue.length ? this.printStockTagQueue() : Promise.resolve();
      printPendingStockTags.finally /* finally so even if Print Stock Tags dialog is cancelled, update to Acumatica may continue*/(() => {
        this.dialog.open(ConfirmationDialog, { data: { message: "Update Acumatica?", confirmText: 'Yes', cancelText: 'No, Cancel' } }).afterClosed().subscribe(response => {
          if (!response) return;

          let docsReleased: Promise<void>[] = [];

          if (this.transferDetails.length > 0) {
            // add a promise to docsReleased for when the Transfers are done (or fail)
            docsReleased.push(new Promise(resolve => {
              let transferDoc = this.createTransferDocument();
              console.log('Transfer Doc: ', transferDoc);
              this.transferService.put(transferDoc, { loader: "Creating Transfer(s)" }).subscribe(
                transerDoc => {
                  this.transferService.Release(transerDoc, { message: "Release Transfer(s)" }).subscribe(
                    () => { resolve(); },
                    err => { resolve(); throw err; }
                  );
                },
                err => { resolve(); throw err; });
            }));
          }

          if (this.adjustmentDetails.length > 0) {
            // add a promise to docsReleased for when the Adjustments are done (or fail)
            docsReleased.push(new Promise(resolve => {
              let adjustmentDoc = this.createAdjustmentDocument();
              console.log('Adjustment Doc: ', adjustmentDoc);
              this.adjustmentService.put(adjustmentDoc, { loader: "Creating Adjustment(s)" }).subscribe(
                adjustmentDoc => {
                  this.adjustmentService.Release(adjustmentDoc, { message: "Release Adjustment(s)" }).subscribe(
                    () => { resolve(); },
                    err => { resolve(); throw err; }
                  );
                },
                err => { resolve(); throw err; });
            }));
          }

          Promise.all(docsReleased).then(() => this.router.navigate(['/']));

        });
      });
    },
    /* cancelled 'transfer unaudited to Lost' - silently disregard */noop
    );
  }
  // #endregion

  // #region Memo sample Overstock Inventory
  overstockChecks: { [locationID: string]: OverstockCheck[] } = {};
  overstockCheckTableCols = ['Audited', 'Thumbnail', 'InventoryID', 'Quantity', 'menu'];
  overstockTableRefresh = () => Object.keys(this.overstockChecks).forEach(locationID => this.overstockChecks[locationID] = this.overstockChecks[locationID].concat());

  @ViewChild('overstockAccordion') OverstockAccordion!: MatAccordion;

  /** boolean value indicating if (true)user is auditing an overstock of an item, or (false) auditing active bin normally */
  // controlled by user having Overstock tab open
  overstockAuditMode = false;
  /** currently open Overstock location ID being audited */
  overstockCheckLocationID?: string = undefined;
  /** open the Overstock expansion panel for the given location ID */
  overstockExpansionPanels: { [key: string]: boolean } = {};

  /** handle Overstock Bin location expansion panel being opened (user indicates which bin they're counting) */
  openOverstockLocationExpansionPanel(locationID: string) {
    if (this.overstockChecks[locationID] === undefined) {
      this.dialog.open(AlertDialog, {
        data: {
          sound: Sound.ErrorBeep,
          message: 'Scanned Location is not among Overstock Locations\nTo full audit this location, switch to [Contents] tab and scan Location ID',
          icon: 'warning',
          buttonText: 'OK'
        },
      });
      return;
    }

    Object.keys(this.overstockChecks).forEach(panelID => {
      this.overstockExpansionPanels[panelID] = (panelID == locationID)
    });
    this.overstockCheckLocationID = locationID;
    setTimeout(() => this.resetItemEntryForm());
  }

  closeOverstockLocationExpansionPanel(locationID: string) {
    this.overstockExpansionPanels[locationID] = false
    this.resetItemEntryForm();
  }

  /** fetch inventory for the given item and add any in Overstock locations to items required to be counted */
  queueOverstockInventory(item: StockItem) {
    // fetch Inventory data for overstock of given stockItem
    let loadingRef = this.snackBar.open("Getting Inventory for " + item.InventoryID);
    this.inventoryInquiryService.getWhseInventory(this.warehouseID, item.InventoryID).subscribe(inventory => {
      loadingRef.dismiss();

      inventory
        .filter(inv =>
          // exclude inventory details for items in the current location
          inv.LocationID != this.locationID
          // only items outside of <UNASSIGNED> location
          && inv.LocationID != '<UNASSIGNED>' && inv.LocationID != null
          // only items with positive quantity available
          && inv.QtyAvailable > 0
          // exclude inventory details already in the Overstock Checks list
          && (this.overstockChecks[inv.LocationID] == undefined
            || this.overstockChecks[inv.LocationID].findIndex(check => check.key == item.InventoryID + '/' + inv.LocationID) == -1)
        )
        // any inventory details remaining need audited
        .forEach(inv =>
          // (initialize an array for this Location if necessary).push(the item needing audited);
          (this.overstockChecks[inv.LocationID!] ||= []).push(new OverstockCheck(item, inv))
        );

      this.overstockTableRefresh();
      // console.warn(this.overstockChecks);
    });
  }

  /** returns true when all Overstock checks for the given Location ID are completed */
  overstockLocationAuditDone(locationID: string): boolean {
    return Object.values(this.overstockChecks[locationID])
      .reduce((prev: boolean, check: OverstockCheck) => prev && check.audited, true);
  }

  /** return a boolean value if the Overstock tab should be visible in the UI */
  get overstockTabVisible(): boolean { return Object.keys(this.overstockChecks).length > 0; }

  /** return the total count of items needing audited from Overstock, displayed in UI tab */
  get overstockItemsPending(): number {
    return Object.keys(this.overstockChecks)
      .reduce((prev: number, locationID: string) =>
        prev + this.overstockChecks[locationID].filter(check => !check.audited).length, 0);
  }

  /** return each unique Overstock Location where items need counted */
  get overstockCheckLocations(): string[] { return Object.keys(this.overstockChecks); }

  // #endregion

}

@Component({ template: "{{message}}" }) class FeedbackSnackbar { message: string = ''; }